import { Component } from '@theme/component';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * Section host that opens the Shop the Look dialog from card image triggers.
 *
 * @extends {Component}
 */
class ShopTheLookSection extends Component {
  /** @type {AbortController | null} */
  #clickController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#clickController = new AbortController();
    this.addEventListener('click', this.#handleClick, { signal: this.#clickController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clickController?.abort();
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('.shop-the-look-card__trigger');
    if (!(trigger instanceof HTMLElement) || !this.contains(trigger)) return;

    event.preventDefault();

    const dialog = this.querySelector('shop-the-look-dialog');
    if (!(dialog instanceof ShopTheLookDialog)) return;

    const productUrl = trigger.dataset.productUrl;
    const lookKey = trigger.dataset.lookKey;
    if (!productUrl || !lookKey) return;

    dialog.openForProduct({
      productUrl,
      lookKey,
      opener: trigger,
    });
  };
}

if (!customElements.get('shop-the-look-section')) {
  customElements.define('shop-the-look-section', ShopTheLookSection);
}

/**
 * Upper bound for a variant fetch before the dialog releases its loading state.
 * Without it, a `variant:update` that never reaches the dialog (detached picker,
 * stale sequence, missing JSON, network error) leaves the details pane
 * permanently dimmed and unclickable.
 */
const VARIANT_LOADING_TIMEOUT = 8000;

/**
 * Shop the Look quick-view dialog.
 *
 * @extends {DialogComponent}
 */
class ShopTheLookDialog extends DialogComponent {
  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {symbol | null} */
  #requestId = null;

  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  /** @type {string | null} */
  #activeProductUrl = null;

  /** @type {HTMLElement | null} */
  #opener = null;

  /** @type {AbortController} */
  #lifecycleController = new AbortController();

  /** @type {number | null} */
  #variantLoadingTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#lifecycleController.signal,
    });
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, {
      signal: this.#lifecycleController.signal,
    });
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal: this.#lifecycleController.signal,
    });
    this.addEventListener('click', this.#handleSelectorClick, {
      signal: this.#lifecycleController.signal,
    });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, {
      signal: this.#lifecycleController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#fetchController?.abort();
    this.#lifecycleController.abort();
    this.#clearVariantLoadingTimer();
  }

  /**
   * @param {{ productUrl: string, lookKey: string, opener?: HTMLElement }} options
   */
  async openForProduct({ productUrl, lookKey, opener }) {
    this.#showLookSelector(lookKey);
    this.#setActiveSelector(productUrl);
    this.showDialog();

    if (opener instanceof HTMLElement) {
      this.#opener = opener;
    }

    await this.#loadProduct(productUrl);
  }

  /**
   * @param {string} lookKey
   */
  #showLookSelector(lookKey) {
    const selectors = this.querySelectorAll('[data-look-selector]');
    for (const selector of selectors) {
      if (!(selector instanceof HTMLElement)) continue;
      selector.hidden = selector.dataset.lookSelector !== lookKey;
    }
  }

  /**
   * @param {string} productUrl
   */
  #setActiveSelector(productUrl) {
    this.#activeProductUrl = productUrl;
    const normalized = this.#normalizeUrl(productUrl);
    const items = this.querySelectorAll('.shop-the-look-modal__selector-item');

    for (const item of items) {
      if (!(item instanceof HTMLElement)) continue;
      const itemUrl = item.dataset.productUrl || '';
      const isActive = this.#normalizeUrl(itemUrl) === normalized;
      item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleSelectorClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const item = target.closest('.shop-the-look-modal__selector-item');
    if (!(item instanceof HTMLElement) || !this.contains(item)) return;

    const productUrl = item.dataset.productUrl;
    if (!productUrl) return;

    if (this.#normalizeUrl(productUrl) === this.#normalizeUrl(this.#activeProductUrl || '')) return;

    this.#setActiveSelector(productUrl);
    this.#loadProduct(productUrl);
  };

  /**
   * Toggle modal loading UI.
   * @param {boolean} isLoading
   * @param {'full' | 'details'} [scope='full'] - `full` dims entire content (product switch);
   *   `details` dims product information only (variant change).
   */
  #setLoading(isLoading, scope = 'full') {
    const content = /** @type {HTMLElement | null} */ (this.refs.content);
    const loading = /** @type {HTMLElement | null} */ (this.refs.loading);
    const body = this.querySelector('.shop-the-look-modal__body');

    this.#clearVariantLoadingTimer();

    if (!isLoading) {
      loading?.toggleAttribute('hidden', true);
      body?.removeAttribute('data-loading');
      content?.removeAttribute('aria-busy');
      content?.removeAttribute('data-variant-loading');
      return;
    }

    if (scope === 'details') {
      loading?.toggleAttribute('hidden', true);
      body?.removeAttribute('data-loading');
      content?.removeAttribute('aria-busy');
      content?.setAttribute('data-variant-loading', '');
      this.#variantLoadingTimer = window.setTimeout(() => {
        this.#setLoading(false);
      }, VARIANT_LOADING_TIMEOUT);
      return;
    }

    content?.removeAttribute('data-variant-loading');
    loading?.toggleAttribute('hidden', false);
    body?.setAttribute('data-loading', 'true');
    content?.setAttribute('aria-busy', 'true');
  }

  #clearVariantLoadingTimer() {
    if (this.#variantLoadingTimer === null) return;
    window.clearTimeout(this.#variantLoadingTimer);
    this.#variantLoadingTimer = null;
  }

  /**
   * @param {string} productUrl
   */
  async #loadProduct(productUrl) {
    const content = /** @type {HTMLElement | null} */ (this.refs.content);
    const error = /** @type {HTMLElement | null} */ (this.refs.error);

    if (!content) return;

    const requestId = Symbol('shop-the-look-load');
    this.#requestId = requestId;
    const cacheKey = this.#normalizeUrl(productUrl);

    error?.toggleAttribute('hidden', true);
    this.#setLoading(true);

    try {
      let productGrid = this.#cachedContent.get(cacheKey);

      if (!productGrid) {
        const html = await this.#fetchProductPage(productUrl);
        if (this.#requestId !== requestId) return;
        if (!html) return;

        const gridElement = html.querySelector('[data-product-grid-content]');
        if (!gridElement) {
          throw new Error('Product content not found');
        }

        productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
        this.#stripExtraContent(productGrid);
        this.#cachedContent.set(cacheKey, productGrid);
      }

      if (this.#requestId !== requestId) return;

      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      this.#linkProductTitle(freshContent, productUrl);
      this.#prepareMobileLayout(freshContent, productUrl);
      // Replace (do not morph) so media-gallery / variant-picker remount and reconnect.
      // Morphing reused the previous media-gallery instance and left color-filter state
      // that collapsed the gallery to a single image on sidebar switches.
      content.replaceChildren(freshContent);
      this.#activeProductUrl = productUrl;
      this.#ensureFullPageLink(content, productUrl);
      this.#resetContentScroll();
      this.#initPickupAvailability(content);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      if (this.#requestId !== requestId) return;
      console.error(err);
      error?.toggleAttribute('hidden', false);
      content.replaceChildren();
    } finally {
      if (this.#requestId !== requestId) return;
      this.#setLoading(false);
    }
  }

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async #fetchProductPage(productPageUrl) {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#fetchController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP ${response.status}`);
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return null;
      }
      throw error;
    }
  }

  /**
   * Reset modal content scroll on open / product switch (not variant change).
   * Also resets nested desktop scroll panes (media + details).
   */
  #resetContentScroll() {
    const content = /** @type {HTMLElement | null} */ (this.refs.content);
    if (!content) return;

    content.scrollTop = 0;
    content.scrollLeft = 0;

    for (const pane of content.querySelectorAll(
      '.product-information__media, .product-details'
    )) {
      if (!(pane instanceof HTMLElement)) continue;
      pane.scrollTop = 0;
      pane.scrollLeft = 0;
    }
  }

  /**
   * Initialize ship/pickup UI inside injected modal content.
   * @param {ParentNode} root
   */
  #initPickupAvailability(root) {
    const run = () => {
      if (typeof window.initPickupAvailability === 'function') {
        window.initPickupAvailability(root);
        return true;
      }
      return false;
    };

    if (run()) return;

    const started = Date.now();
    const timer = window.setInterval(() => {
      if (run() || Date.now() - started > 3000) {
        window.clearInterval(timer);
      }
    }, 50);
  }

  /**
   * Show product-information loading while variant-picker fetches updated section HTML.
   * @param {Event} event
   */
  #handleVariantSelected = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;
    this.#setLoading(true, 'details');
  };

  /**
   * Re-apply pickup after variant morph wipes injected chrome.
   * @param {Event} event
   */
  #handleVariantUpdate = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;

    const content = /** @type {HTMLElement | null} */ (this.refs.content);
    if (!content) return;

    this.#initPickupAvailability(content);
    this.#ensureFullPageLink(content, this.#activeProductUrl || '');
    this.#setLoading(false);
  };

  /**
   * Hide non-purchase chrome that CSS may not fully suppress.
   *
   * These nodes are hidden rather than removed: `morph` aligns children by index, so
   * dropping a direct child of `.product-details` shifts every sibling and makes a
   * variant morph detach live elements from the dialog.
   * @param {Element} productGrid
   */
  #stripExtraContent(productGrid) {
    const selectors = [
      '.breadcrumb',
      '.product-breadcrumbs',
      'nav[aria-label="breadcrumb"]',
      '.view-more-details__wrapper',
      '.skip-to-content-link',
      // Quick-view-only title; the popup shows the PDP title block instead
      '.view-product-title',
    ];

    for (const selector of selectors) {
      for (const node of productGrid.querySelectorAll(selector)) {
        node.setAttribute('data-shop-the-look-hidden', '');
      }
    }
  }

  /**
   * Wrap the PDP title block in a link so the popup keeps the PDP typography.
   * @param {Element} productGrid
   * @param {string} productUrl
   */
  #linkProductTitle(productGrid, productUrl) {
    const titleBlock = productGrid.querySelector('.vendor-title-text');
    if (!titleBlock || titleBlock.closest('.shop-the-look-modal__title-link')) return;

    const link = document.createElement('a');
    link.className = 'shop-the-look-modal__title-link';
    link.href = productUrl;
    titleBlock.replaceWith(link);
    link.appendChild(titleBlock);
  }

  /**
   * Mirrors quick-add mobile reordering so title/price/form remain usable.
   * Preserves rewards + description accordions after stripping .product-details.
   * @param {Element} productGrid
   * @param {string} productUrl
   */
  #prepareMobileLayout(productGrid, productUrl) {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const productDetails = productGrid.querySelector('.product-details');
    const productFormComponent = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');
    const titleLink = productGrid.querySelector('.shop-the-look-modal__title-link');
    const titleSource = productGrid.querySelector('.product-title') || productGrid.querySelector('h1, h2');

    const rewardsEl =
      productDetails?.querySelector('[id*="saint_bernard_powerups_rewards"]') ||
      productDetails?.querySelector('.rewards__product') ||
      productDetails?.querySelector('.saint-bernard-rewards');
    const rewardsBlock = rewardsEl?.closest('[id*="saint_bernard_powerups_rewards"]') || rewardsEl;
    const description = productDetails?.querySelector('.custom-product-description');
    // Move the custom-liquid wrapper so the snippet's inline <style> stays with the accordions
    const descriptionBlock = description?.parentElement || description;
    const vendorTitle = productDetails?.querySelector('.vendor-title');

    const productHeader = document.createElement('div');
    productHeader.classList.add('product-header');

    if (vendorTitle) {
      productHeader.appendChild(vendorTitle);
    }

    if (titleLink) {
      productHeader.appendChild(titleLink);
    } else if (titleSource) {
      const productTitle = document.createElement('a');
      productTitle.textContent = titleSource.textContent?.trim() || '';
      productTitle.href = productUrl;
      productHeader.appendChild(productTitle);
    }

    if (productPrice) {
      productHeader.appendChild(productPrice);
    }

    productGrid.appendChild(productHeader);

    if (rewardsBlock) {
      productGrid.appendChild(rewardsBlock);
    }

    if (variantPicker) {
      productGrid.appendChild(variantPicker);
    }
    if (productFormComponent) {
      productGrid.appendChild(productFormComponent);
    }

    if (descriptionBlock) {
      productGrid.appendChild(descriptionBlock);
    }

    productDetails?.remove();
  }

  /**
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    const detail = /** @type {{ data?: { didError?: boolean } } | undefined} */ (
      /** @type {CustomEvent} */ (event).detail
    );
    if (detail?.data?.didError) return;
    this.closeDialog();
  };

  #handleDialogClose = () => {
    this.#fetchController?.abort();
    this.#requestId = null;
    this.#activeProductUrl = null;

    const error = /** @type {HTMLElement | null} */ (this.refs.error);
    this.#setLoading(false);
    error?.toggleAttribute('hidden', true);

    if (this.#opener instanceof HTMLElement) {
      this.#opener.focus();
      this.#opener = null;
    }
  };

  /**
   * Add or restore the explicit PDP link below the inline description.
   * @param {ParentNode} root
   * @param {string} productUrl
   */
  #ensureFullPageLink(root, productUrl) {
    if (!productUrl) return;

    const dialog = /** @type {HTMLElement | null} */ (this.refs.dialog);
    const label = dialog?.dataset.fullPageLabel || 'View full product page';

    let link = root.querySelector('.shop-the-look-modal__full-page-link');
    if (!(link instanceof HTMLAnchorElement)) {
      link = document.createElement('a');
      link.className = 'shop-the-look-modal__full-page-link';
      link.textContent = label;
    }

    link.href = productUrl;

    const description = root.querySelector('.custom-product-description');
    if (description instanceof Element) {
      const wrapper = description.parentElement;
      const insertAfter =
        wrapper instanceof Element &&
        !wrapper.classList.contains('product-details') &&
        !wrapper.hasAttribute('data-product-grid-content')
          ? wrapper
          : description;
      insertAfter.after(link);
      return;
    }

    const after =
      root.querySelector('product-form-component') || root.querySelector('.product-details');
    if (after instanceof Element) {
      after.after(link);
      return;
    }

    if (root instanceof Element) {
      root.appendChild(link);
    }
  }

  /**
   * @param {string} url
   */
  #normalizeUrl(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.search = '';
      parsed.hash = '';
      return parsed.pathname;
    } catch {
      return url;
    }
  }
}

if (!customElements.get('shop-the-look-dialog')) {
  customElements.define('shop-the-look-dialog', ShopTheLookDialog);
}
